'use client';

import { useTranslation } from 'react-i18next';
import type { ZodError } from 'zod';
import { AlertTriangle } from 'lucide-react';
import { Button } from 'v5/design-system/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import { StopPropagation } from 'v5/platform/react/StopPropagation';
import { ZodValidationDetailsBody } from 'v5/patterns/feedback/ErrorAlert';
import { formatZodErrorIssues } from './rowValidationDisplay';

export type EntityListRowValidationIndicatorProps = {
  error: ZodError;
  rawItem: unknown | undefined;
  /** `icon` — compact amber triangle for simple invalid rows; `link` — text trigger for advanced panel. */
  variant?: 'icon' | 'link';
  className?: string;
};

function ValidationDetailsPopoverContent({
  error,
  rawItem,
  title
}: {
  error: ZodError;
  rawItem: unknown | undefined;
  title: string;
}) {
  return (
    <PopoverContent
      side="bottom"
      align="start"
      className="max-w-[min(36rem,calc(100vw-2rem))] w-[max(var(--radix-popover-trigger-width),min(36rem,var(--radix-popover-content-available-width)))] p-0 text-xs text-fg-default"
    >
      <ValidationDetailsHeader title={title} />
      <div className="p-4">
        <ZodValidationDetailsBody zodError={error} data={rawItem ?? {}} />
      </div>
    </PopoverContent>
  );
}

function ValidationDetailsHeader({ title }: { title: string }) {
  return (
    <div className="border-b border-border-default bg-bg-subtle px-4 py-2">
      <p className="text-sm font-medium text-fg-default">{title}</p>
    </div>
  );
}

export function EntityListRowValidationIndicator({
  error,
  rawItem,
  variant = 'icon',
  className
}: EntityListRowValidationIndicatorProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'EntityListRow' });
  const issueCount = error.issues.length;
  const tooltipLabel = `${t('validationFailed')} (${issueCount})`;
  const ariaLabel = `${tooltipLabel}. ${t('validationDetailsTitle')}`;
  const detailsTitle = t('validationDetailsTitle');

  const popoverContent = (
    <ValidationDetailsPopoverContent
      error={error}
      rawItem={rawItem}
      title={detailsTitle}
    />
  );

  if (variant === 'link') {
    return (
      <StopPropagation>
        <Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  aria-label={ariaLabel}
                  className={cn(className)}
                >
                  <span className="inline-flex max-w-full min-w-0 flex-wrap items-baseline gap-x-1 whitespace-normal tabular-nums">
                    <span className="min-w-0 break-words font-medium text-fg-danger">
                      {t('validationFailed')}
                    </span>
                    <span className="shrink-0 text-xs font-normal text-fg-muted">
                      ({issueCount})
                    </span>
                  </span>
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">{tooltipLabel}</TooltipContent>
          </Tooltip>
          {popoverContent}
        </Popover>
      </StopPropagation>
    );
  }

  return (
    <StopPropagation>
      <div className="contents">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              layout="icon"
              size="xs"
              aria-label={ariaLabel}
              className={cn(
                'size-5 min-h-5 p-0',
                'text-amber-600 hover:text-amber-700 dark:text-amber-500',
                className
              )}
            >
              <AlertTriangle className="size-4" strokeWidth={2} />
            </Button>
          </PopoverTrigger>
          {popoverContent}
        </Popover>
      </div>
    </StopPropagation>
  );
}
